import { supabase } from '../lib/supabase'
import type {
  DiagramData,
  DiagramRow,
  DiagramUpdate,
  DiagramListRow,
} from '../types/database.types'

/**
 * Service for managing diagrams in Supabase
 */

// List all diagrams for the current user
export const listUserDiagrams = async (): Promise<{
  data: DiagramListRow[] | null
  error: Error | null
}> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase
      .from('diagrams')
      .select('id, title, description, thumbnail, is_public, is_template, created_at, updated_at')
      .eq('user_id', user.id) // Only get current user's diagrams
      .order('updated_at', { ascending: false })

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error listing diagrams:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to list diagrams'),
    }
  }
}

// Get a specific diagram by ID
export const getDiagram = async (
  id: string
): Promise<{
  data: DiagramRow | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase
      .from('diagrams')
      .select('*')
      .eq('id', id)
      .single()

    if (error) {
      // PGRST116 means "no rows found" - this is expected for new diagrams
      // Don't treat it as an error, just return null data
      if (error.code === 'PGRST116') {
        return { data: null, error: null }
      }
      throw error
    }

    return { data, error: null }
  } catch (error) {
    console.error('Error getting diagram:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to get diagram'),
    }
  }
}

// Create a new diagram
export const createDiagram = async (
  diagramData: DiagramData,
  title?: string,
  description?: string,
  thumbnail?: string,
  id?: string
): Promise<{
  data: DiagramRow | null
  error: Error | null
}> => {
  try {
    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('User not authenticated. Please login to save diagrams.')
    }

    const insertData: any = {
      user_id: user.id,
      title: title || diagramData.title || 'Untitled Diagram',
      description: description || diagramData.description || null,
      data: diagramData,
      thumbnail: thumbnail || null,
      is_public: false,
    }

    // If an ID is provided, include it in the insert
    if (id) {
      insertData.id = id
    }

    const { data, error } = await supabase
      .from('diagrams')
      .insert(insertData)
      .select()
      .single()

    if (error) {
      console.error('Supabase error details:', error)
      throw new Error(`Failed to create diagram: ${error.message || 'Database error'}`)
    }

    return { data, error: null }
  } catch (error) {
    console.error('Error creating diagram:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to create diagram'),
    }
  }
}

// Update an existing diagram
export const updateDiagram = async (
  id: string,
  diagramData: DiagramData,
  title?: string,
  description?: string,
  thumbnail?: string
): Promise<{
  data: DiagramRow | null
  error: Error | null
}> => {
  try {
    const updateData: DiagramUpdate = {
      title: title || diagramData.title,
      description: description || diagramData.description || null,
      data: diagramData,
      thumbnail: thumbnail || null,
    }

    const { data, error } = await supabase
      .from('diagrams')
      // @ts-expect-error - Supabase type inference issue with placeholder env variables
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) {
      console.error('Supabase error details:', error)
      // Include the error code (PGRST116) in the message so SaveButton can detect it
      const errorMessage = error.code === 'PGRST116'
        ? `PGRST116: ${error.message}`
        : `Failed to update diagram: ${error.message || 'Database error'}`
      throw new Error(errorMessage)
    }

    return { data, error: null }
  } catch (error) {
    console.error('Error updating diagram:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to update diagram'),
    }
  }
}

// Delete a diagram
export const deleteDiagram = async (
  id: string
): Promise<{
  success: boolean
  error: Error | null
}> => {
  try {
    const { error } = await supabase.from('diagrams').delete().eq('id', id)

    if (error) throw error

    return { success: true, error: null }
  } catch (error) {
    console.error('Error deleting diagram:', error)
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Failed to delete diagram'),
    }
  }
}

// Toggle diagram public status
export const toggleDiagramPublic = async (
  id: string,
  isPublic: boolean
): Promise<{
  data: DiagramRow | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase
      .from('diagrams')
      // @ts-expect-error - Supabase type inference issue with placeholder env variables
      .update({ is_public: isPublic })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error toggling diagram public status:', error)
    return {
      data: null,
      error:
        error instanceof Error ? error : new Error('Failed to update diagram visibility'),
    }
  }
}

// Duplicate a diagram
export const duplicateDiagram = async (
  id: string
): Promise<{
  data: DiagramRow | null
  error: Error | null
}> => {
  try {
    // Get the original diagram
    const { data: original, error: fetchError } = await getDiagram(id)

    if (fetchError || !original) {
      throw fetchError || new Error('Diagram not found')
    }

    // Create a copy with a new title
    const copyTitle = `${original.title} (Copy)`

    return await createDiagram(original.data, copyTitle, original.description || undefined)
  } catch (error) {
    console.error('Error duplicating diagram:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to duplicate diagram'),
    }
  }
}

// Get diagram count for current user
export const getUserDiagramCount = async (): Promise<{
  count: number
  error: Error | null
}> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('User not authenticated')
    }

    const { count, error } = await supabase
      .from('diagrams')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)

    if (error) throw error

    return { count: count || 0, error: null }
  } catch (error) {
    console.error('Error getting diagram count:', error)
    return {
      count: 0,
      error: error instanceof Error ? error : new Error('Failed to get diagram count'),
    }
  }
}

// Search diagrams by title
export const searchDiagrams = async (
  query: string
): Promise<{
  data: DiagramListRow[] | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase
      .from('diagrams')
      .select('id, title, description, thumbnail, is_public, is_template, created_at, updated_at')
      .ilike('title', `%${query}%`)
      .order('updated_at', { ascending: false })

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error searching diagrams:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to search diagrams'),
    }
  }
}

// List all public diagrams
export const listPublicDiagrams = async (): Promise<{
  data: DiagramListRow[] | null
  error: Error | null
}> => {
  try {
    const { data, error } = await supabase
      .from('diagrams')
      .select('id, title, description, thumbnail, is_public, is_template, created_at, updated_at')
      .eq('is_public', true)
      .order('updated_at', { ascending: false })

    if (error) throw error

    return { data, error: null }
  } catch (error) {
    console.error('Error listing public diagrams:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to list public diagrams'),
    }
  }
}

// Clone a public diagram to the current user's account
export const clonePublicDiagram = async (
  id: string
): Promise<{
  data: DiagramRow | null
  error: Error | null
}> => {
  try {
    // Get the public diagram (anyone can read public diagrams)
    const { data: original, error: fetchError } = await getDiagram(id)

    if (fetchError || !original) {
      throw fetchError || new Error('Diagram not found')
    }

    // Verify the diagram is actually public
    if (!original.is_public) {
      throw new Error('This diagram is not public and cannot be cloned')
    }

    // Get current user
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) {
      throw new Error('User not authenticated')
    }

    // Create a copy for the current user
    const copyTitle = original.is_template
      ? original.title // Keep original title for templates
      : `${original.title} (Copy)` // Add (Copy) for user diagrams

    return await createDiagram(
      original.data,
      copyTitle,
      original.description || undefined,
      original.thumbnail || undefined
    )
  } catch (error) {
    console.error('Error cloning public diagram:', error)
    return {
      data: null,
      error: error instanceof Error ? error : new Error('Failed to clone diagram'),
    }
  }
}
